import { useEffect, useRef, useCallback, useState } from 'react';
import { useBroadcastChannel } from './useBroadcastChannel';

/**
 * Optimistic Sync Hook
 *
 * Replaces conflict-based save system with optimistic sync:
 * - Server is always source of truth
 * - Save on blur/navigation/visibility change (natural breakpoints)
 * - Multi-tab coordination via BroadcastChannel
 * - Last write wins with toast notification
 * - No conflict dialogs
 *
 * @param {Object} options
 * @param {string} options.scenarioId - Current scenario ID
 * @param {string} options.projectId - Current project ID
 * @param {boolean} options.isDirty - External dirty state (from parent component)
 * @param {Function} options.getPayload - Function that returns current state to save
 * @param {Function} options.saveFn - Async function to perform the save (receives payload, signal)
 * @param {Function} options.onRemoteUpdate - Callback when another tab updates this scenario
 * @param {Function} options.fetchScenario - Function to fetch fresh scenario data from server
 * @returns {Object} { saveStatus, lastSavedAt, saveNow, markClean, debouncedSave }
 */
export function useOptimisticSync({
  scenarioId,
  projectId,
  isDirty, // Accept isDirty from parent instead of managing internally
  getPayload,
  saveFn,
  onRemoteUpdate,
  fetchScenario,
}) {
  // Save status state
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Refs for managing save state
  const abortControllerRef = useRef(null);
  const saveInProgressRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const lastSavedTimestampRef = useRef(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Track if we're currently fetching (to ignore broadcasts during our own save)
  const isSavingRef = useRef(false);

  // BroadcastChannel for cross-tab coordination
  const handleBroadcastMessage = useCallback((message) => {
    // Ignore broadcasts while we're actively saving
    if (isSavingRef.current) {
      console.log('[OptimisticSync] Ignoring broadcast during active save');
      return;
    }

    if (message.type === 'updated' && message.timestamp) {
      // Another tab saved - check if their version is newer
      const remoteTime = new Date(message.timestamp).getTime();
      const localTime = lastSavedTimestampRef.current
        ? new Date(lastSavedTimestampRef.current).getTime()
        : 0;

      if (remoteTime > localTime) {
        console.log('[OptimisticSync] Remote update detected, refreshing data');
        // Fetch fresh data and notify parent
        if (fetchScenario && onRemoteUpdate) {
          fetchScenario().then((freshData) => {
            if (freshData) {
              lastSavedTimestampRef.current = message.timestamp;
              setLastSavedAt(message.timestamp);
              setSaveStatus('saved');
              onRemoteUpdate(freshData);
            }
          });
        }
      }
    }
  }, [fetchScenario, onRemoteUpdate]);

  const { broadcast } = useBroadcastChannel(
    scenarioId ? `scenario-${scenarioId}` : null,
    handleBroadcastMessage
  );

  /**
   * Perform the actual save operation
   */
  const performSave = useCallback(async (signal) => {
    if (!scenarioId || scenarioId === 'new' || !saveFn) return false;

    const payload = getPayload();
    if (!payload) return false;

    try {
      isSavingRef.current = true;
      saveInProgressRef.current = true;
      setSaveStatus('saving');

      const result = await saveFn(payload, signal);

      if (result && result.updated_at) {
        // Update local state
        lastSavedTimestampRef.current = result.updated_at;
        setLastSavedAt(result.updated_at);
        setSaveError(null);
        setSaveStatus('saved');
        retryCountRef.current = 0;

        // Broadcast to other tabs
        broadcast({ type: 'updated', timestamp: result.updated_at });

        console.log('[OptimisticSync] Save completed:', result.updated_at);
        return true;
      }

      return false;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[OptimisticSync] Save aborted');
        return false;
      }

      console.error('[OptimisticSync] Save failed:', error);

      // Retry with exponential backoff
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const delay = Math.pow(2, retryCountRef.current) * 1000;
        console.log(`[OptimisticSync] Retrying in ${delay}ms (attempt ${retryCountRef.current})`);
        setSaveError(error.message);
        setSaveStatus('error');

        setTimeout(() => {
          if (!abortControllerRef.current?.signal?.aborted) {
            performSave(abortControllerRef.current?.signal);
          }
        }, delay);
      } else {
        setSaveError(error.message);
        setSaveStatus('error');
        retryCountRef.current = 0;
      }

      return false;
    } finally {
      isSavingRef.current = false;
      saveInProgressRef.current = false;
    }
  }, [scenarioId, saveFn, getPayload, broadcast]);

  /**
   * Debounced save - called on blur events
   * Waits 500ms to batch rapid field changes
   */
  const debouncedSave = useCallback(() => {
    if (!isDirty) return;

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Abort any in-flight save
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    debounceTimeoutRef.current = setTimeout(() => {
      abortControllerRef.current = new AbortController();
      performSave(abortControllerRef.current.signal);
    }, 500);
  }, [isDirty, performSave]);

  /**
   * Immediate save - called on visibility change, beforeunload
   */
  const saveNow = useCallback(async () => {
    if (!isDirty) return;

    // Clear debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Abort any in-flight save
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    await performSave(abortControllerRef.current.signal);
  }, [isDirty, performSave]);

  /**
   * Mark state as clean (after successful save or fresh load)
   * Called by parent when scenario is loaded from server
   */
  const markClean = useCallback((timestamp) => {
    if (timestamp) {
      lastSavedTimestampRef.current = timestamp;
      setLastSavedAt(timestamp);
    }
    setSaveStatus('saved');
  }, []);

  // Handle visibility change (save when tab becomes hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isDirty) {
        console.log('[OptimisticSync] Tab hidden, saving...');
        saveNow();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isDirty, saveNow]);

  // Note: beforeunload save removed - sendBeacon cannot include auth headers
  // The visibility change handler (above) will catch tab switches before unload
  // For browser close, we accept that unsaved changes may be lost (user is warned by isDirty indicator)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    saveStatus,
    saveError,
    lastSavedAt,
    saveNow,
    markClean,
    debouncedSave, // For form onBlur handler
  };
}
